import { FieldArray } from 'formik'
import React, { RefObject, useEffect, useState, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { Column, FormSubGroup, FormSubGroupHeader, FormSubGroupHeading, Row, Text } from '../layout'
import { JobItemToolbar } from './JobItemToolbar'
import { Totals } from './Totals'
import { TowingRecordItem } from './TowingRecordItem'
import { DiscountItem } from './DiscountItem'
import { JobItemInput } from './types'
import { ItemVatCategory, JobItemDiscountType, JobItemType } from '../../common/types'
import { useItemGroups, useItems } from './hooks'
import { messages } from './messages'
import { formatMessageSafe } from '../../util/translateInputOptions'

interface Props {
  jobItems: JobItemInput[]
  discountItem: JobItemInput | null
  itemsIncludeVat: boolean
  setValue: (field: string, value: any) => void
  fieldArrayName?: string
  towingRecordId?: number
  disabled?: boolean
  paymentRef?: RefObject<any>
  scrollToPayment?: boolean
}

export const TowingRecordItems: React.FunctionComponent<Props> = ({
  towingRecordId,
  itemsIncludeVat,
  jobItems,
  setValue,
  fieldArrayName = 'jobItems',
  discountItem,
  disabled = false,
  paymentRef = null,
  scrollToPayment = false,
}) => {

  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)

  const { itemGroups, loading: loadingItemGroups } = useItemGroups()
  const { items, loading: loadingItems } = useItems()

  const [shouldScroll, setShouldScroll] = useState<boolean>(scrollToPayment)
  const createDiscountItem = () => {
    setValue('discountItem.id', null)
    setValue('discountItem.type', JobItemType.discount)
    setValue('discountItem.description', '')
    setValue('discountItem.discount', 0)
    setValue('discountItem.discountType', JobItemDiscountType.amount)
    setValue('discountItem.grossAmount', 0)
    setValue('discountItem.name', formatMessage(messages.discountItemName))
    setValue('discountItem.netAmount', 0)
    setValue('discountItem.quantity', 1)
    setValue('discountItem.unit', '')
    setValue('discountItem.unitPrice', 0)
    setValue('discountItem.vat', 25.5)
    setValue('discountItem.unitPriceHasVatIncluded', true)
    setValue('discountItem.vatAmount', 25.5)
    setValue('discountItem.vatCategory', ItemVatCategory.standard_new)
  }

  const deleteDiscountItem = () => {
    setValue('discountItem', null)
  }

  const setIncludeVat = (includeVat: boolean) => {
    setValue('itemsIncludeVat', includeVat)
  }

  const handleScroll = useCallback(() => {
    if (paymentRef) {
      paymentRef.current.scrollIntoView()
    }

    setShouldScroll(false)
  }, [paymentRef, setShouldScroll])

  useEffect(() => {
    if (shouldScroll) {
      handleScroll()
    }
  }, [shouldScroll, handleScroll])

  const hasAutomaticItems = jobItems.some(item => item.type === JobItemType.automatic || item.type === JobItemType.generated)
  return (
    <div ref={paymentRef}>
      <FormSubGroupHeader padding="0.5rem 0 1rem 0" onClick={() => handleScroll()}>
        <Row>
          <Column>
            <FormSubGroupHeading>{formatMessage(messages.paymentLinesHeader)}</FormSubGroupHeading>
          </Column>
          <Column></Column>
        </Row>
      </FormSubGroupHeader>
      <FormSubGroup padding="0.5rem 0 1rem 0">
        <FieldArray name={fieldArrayName}>
          {arrayHelpers => {
            const push = (item: JobItemInput) => {
              arrayHelpers.push(item)
            }
            const remove = (index: number) => {
              arrayHelpers.remove(index)
            }
            const towingRecordItems =
              jobItems && jobItems.length > 0
                ? jobItems.map((jobItem: JobItemInput, index: number) => (
                    <TowingRecordItem
                      key={index}
                      jobItem={jobItem}
                      index={index}
                      removeItem={() => remove(index)}
                      itemsIncludeVat={itemsIncludeVat}
                      fieldArrayName={fieldArrayName}
                      towingRecordId={towingRecordId}
                      disabled={disabled}
                    />
                  ))
                : null
            return (
              <>
                <JobItemToolbar
                  items={items}
                  itemGroups={itemGroups}
                  createJobItem={push}
                  createDiscountItem={createDiscountItem}
                  hasDiscountItem={discountItem ? true : false}
                  setIncludeVat={setIncludeVat}
                  itemsIncludeVat={itemsIncludeVat}
                  disabled={disabled}
                  loadingItemGroups={loadingItemGroups}
                  loadingItems={loadingItems}
                />
                {towingRecordItems}

                <Text style={{ margin: '0.5rem 0 0 0' }}>
                  {hasAutomaticItems && formatMessage(messages.automaticItemsNotice)}
                </Text>
              </>
            )
          }}
        </FieldArray>
        {discountItem && (
          <DiscountItem
            jobItem={discountItem}
            removeItem={deleteDiscountItem}
            towingRecordId={towingRecordId}
            itemsIncludeVat={true}
            disabled={disabled}
          />
        )}
      </FormSubGroup>
      <FormSubGroup>
        <Totals jobItems={jobItems} itemsIncludeVat={itemsIncludeVat} discountItem={discountItem} />
      </FormSubGroup>
    </div>
  )
}
